import { connect } from 'react-redux';
import { TenantPriceList } from '../../../../models/Entities/TenantPriceLists/TenantPriceList';
import { MessageBarType, PrimaryButton } from '@fluentui/react';
import {
  Badge,
  Button,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  SearchBox,
  Spinner,
  TableColumnDefinition,
  Text,
  Tooltip,
  createTableColumn
} from '@fluentui/react-components';
import {
  Add24Regular,
  EditRegular,
  InfoRegular,
  LockClosedRegular,
  LockOpenRegular,
  ClockRegular,
  TagMultiple20Regular
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import Moment from 'react-moment';
import { useNavigate } from 'react-router-dom';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import TenantPriceListFormComponent from '../TenantPriceListForm/TenantPriceListFormComponent';
import { useBoolean } from '@fluentui/react-hooks';

// Functions
import * as ComponentFunctions from './TenantPriceListFunctions';

////i18Next
import { useTranslation } from 'react-i18next';

// Styles
import './TenantPriceList.css';
import { ConfirmationButtonComponent } from '../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';

export const TenantPriceListComponent = (props: ComponentFunctions.Props) => {
  const navigate = useNavigate();
  const [selectedTenantPriceList, setSelectedTenantPriceList] =
    useState<TenantPriceList>();
  const [filter, setFilter] = useState('');

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);

  const onViewTenantPriceListEvent = (_list: TenantPriceList) => {
    navigate('/tenantpricelist/' + _list.id);
  };

  //useTranslation()
  const [t] = useTranslation();

  useEffect(() => {
    props.GetAllTenantPriceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // events

  const onAddTenantPriceListEvent = () => {
    setSelectedTenantPriceList(undefined);
    openPanel();
  };

  const onEditTenantPriceListEvent = (PriceList: TenantPriceList) => {
    setSelectedTenantPriceList(PriceList);
    openPanel();
  };

  const onDeleteTenantPriceListEvent = (PriceList: TenantPriceList) => {
    props.DeleteTenantPriceList(PriceList.id);
  };

  // data

  const allPriceLists = useMemo(
    () => props.TenantPriceLists || [],
    [props.TenantPriceLists]
  );

  const visiblePriceLists = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return allPriceLists;
    return allPriceLists.filter((item: TenantPriceList) => {
      const name = (item.priceList?.listName || '').toLowerCase();
      const comments = (item.priceList?.comments || '').toLowerCase();
      return name.includes(term) || comments.includes(term);
    });
  }, [allPriceLists, filter]);

  // render

  const columns = useMemo((): TableColumnDefinition<TenantPriceList>[] => [
    createTableColumn<TenantPriceList>({
      columnId: 'name',
      compare: (a, b) =>
        (a.priceList?.listName || '').localeCompare(b.priceList?.listName || '', undefined, {
          sensitivity: 'base',
          numeric: true
        }),
      renderHeaderCell: () => t('tenantPriceList.name'),
      renderCell: (item) => (
        <div className="tpl-name">
          <Tooltip
            content={
              item.isBlocked
                ? t('tenantPriceList.blocked.yes')
                : t('tenantPriceList.blocked.no')
            }
            relationship="label"
            withArrow
          >
            <span
              className={`tpl-lock ${item.isBlocked ? 'tpl-lock--closed' : 'tpl-lock--open'}`}
            >
              {item.isBlocked ? <LockClosedRegular /> : <LockOpenRegular />}
            </span>
          </Tooltip>
          <div className="tpl-name__text">
            <Text weight="semibold" className="tpl-name__title">
              {item.priceList?.listName}
            </Text>
            <Text size={200} className="tpl-name__type">
              {t(ComponentFunctions.getTenantPriceListTypeKey(item.priceListType))}
            </Text>
          </div>
        </div>
      )
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'status',
      compare: (a, b) =>
        ComponentFunctions.getStatus(a).status.localeCompare(
          ComponentFunctions.getStatus(b).status
        ),
      renderHeaderCell: () => t('tenantPriceList.status.label'),
      renderCell: (item) => {
        const status = ComponentFunctions.getStatus(item);
        switch (status.status) {
          case 'Expired':
            return (
              <Badge appearance="filled" color="danger" size="small" icon={<ClockRegular />}>
                {t('tenantPriceList.status.expired')}
              </Badge>
            );
          case 'Pending':
            return (
              <Badge appearance="filled" color="warning" size="small">
                {t('tenantPriceList.status.pending')}
              </Badge>
            );
          // a list with no dates at all was falling through to "Vigente"
          case 'NoValidity':
            return (
              <Badge appearance="outline" color="informative" size="small">
                {t('tenantPriceList.status.noValidity')}
              </Badge>
            );
          default:
            return (
              <Badge appearance="outline" color="success" size="small">
                {t('tenantPriceList.status.active')}
              </Badge>
            );
        }
      }
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'comment',
      renderHeaderCell: () => t('tenantPriceList.comment'),
      renderCell: (item) =>
        item.priceList?.comments ? (
          <Text className="tpl-comment" title={item.priceList.comments}>
            {item.priceList.comments}
          </Text>
        ) : (
          <Text className="tpl-muted">—</Text>
        )
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'validSince',
      compare: (a, b) =>
        new Date(a.effectiveDateStart ?? 0).getTime() -
        new Date(b.effectiveDateStart ?? 0).getTime(),
      renderHeaderCell: () => t('tenantPriceList.validSince'),
      renderCell: (item) =>
        item.effectiveDateStart ? (
          <Moment format="DD/MM/YYYY">{item.effectiveDateStart}</Moment>
        ) : (
          <span className="tpl-muted">{t('tenantPriceList.notDefined')}</span>
        )
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'validUntil',
      compare: (a, b) =>
        new Date(a.effectiveDateEnd ?? 0).getTime() -
        new Date(b.effectiveDateEnd ?? 0).getTime(),
      renderHeaderCell: () => t('tenantPriceList.validityUntil'),
      renderCell: (item) =>
        item.effectiveDateEnd ? (
          <Moment format="DD/MM/YYYY">{item.effectiveDateEnd}</Moment>
        ) : (
          <span className="tpl-muted">{t('tenantPriceList.notDefined')}</span>
        )
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'options',
      renderHeaderCell: () => t('tenantPriceList.options'),
      renderCell: (item) => (
        <div className="SectionControls tpl-actions">
          <Button
            appearance="subtle"
            icon={<InfoRegular />}
            title={t('tenantPriceList.buttons.detailButton')}
            aria-label={t('tenantPriceList.buttons.detailButton')}
            onClick={() => onViewTenantPriceListEvent(item)}
          />
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            title={t('tenantPriceList.buttons.editButton')}
            aria-label={t('tenantPriceList.buttons.editButton')}
            onClick={() => onEditTenantPriceListEvent(item)}
          />
          <div className="danger">
            <ConfirmationButtonComponent
              ButtonId={'MedicalCenterPriceListDeleteButton' + item.id}
              ButtonIconName={'Delete'}
              ButtonText={''}
              ButtonCssClass={''}
              ConfirmationButtonText={t('tenantPriceList.buttons.deleteButton')}
              ConfirmationButtonCssClass={'deleteButton'}
              CancelButtonText={t('tenantPriceList.buttons.cancelButton')}
              CancelButtonCssClass={''}
              Title={t('tenantPriceList.buttons.title')}
              Description={t('tenantPriceList.buttons.description')}
              ElementTextHeader={t('tenantPriceList.buttons.priceList')}
              ElementTextInformation={item.priceList?.listName}
              onConfirmation={() => onDeleteTenantPriceListEvent(item)}
              onCancel={() => {}}
              isOperationExecuting={props.isDeleting || false}
              isOperationExecutedWithError={props.failOnDeleting || false}
              OperationError={props.error}
            />
          </div>
        </div>
      )
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, props.isDeleting, props.failOnDeleting, props.error]);

  const renderBody = () => {
    if (props.isGettingAll && allPriceLists.length === 0) {
      return (
        <div className="tpl-state">
          <Spinner size="tiny" />
          <span>{t('tenantPriceList.loading.priceList')}</span>
        </div>
      );
    }

    if (props.failOnGettingAll && allPriceLists.length === 0) {
      return (
        <div className="tpl-state tpl-state--empty">
          {t('tenantPriceList.state.loadFailed')}
        </div>
      );
    }

    if (allPriceLists.length === 0) {
      return (
        <div className="tpl-state tpl-state--empty">
          {t('tenantPriceList.state.empty')}
        </div>
      );
    }

    if (visiblePriceLists.length === 0) {
      return (
        <div className="tpl-state tpl-state--empty">
          {t('tenantPriceList.state.noMatches', { term: filter })}
        </div>
      );
    }

    return (
      <div className="tpl-grid-wrap">
        <DataGrid
          items={visiblePriceLists}
          columns={columns}
          getRowId={(item) => item.id.toString()}
          size="small"
          sortable
          focusMode="composite"
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<TenantPriceList>>
            {({ item, rowId }) => (
              <DataGridRow<TenantPriceList>
                key={rowId}
                className={`tpl-row tpl-row--${ComponentFunctions.getStatus(item).status.toLowerCase()}`}
              >
                {(column) => <DataGridCell>{column.renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    );
  };

  return (
    <section className="tpl-card">
      <header className="tpl-card__header">
        <span className="tpl-card__icon">
          <TagMultiple20Regular />
        </span>
        <h3 className="tpl-card__title">{t('tenantPriceList.title')}</h3>
        <Badge appearance="tint" color="informative" shape="rounded">
          {filter.trim() && visiblePriceLists.length !== allPriceLists.length
            ? `${visiblePriceLists.length} / ${allPriceLists.length}`
            : allPriceLists.length}
        </Badge>
        {props.isGettingAll && allPriceLists.length > 0 && (
          <Spinner size="extra-tiny" className="tpl-card__spinner" />
        )}

        <div className="tpl-card__actions">
          <SearchBox
            className="tpl-search"
            placeholder={t('tenantPriceList.state.search')}
            value={filter}
            onChange={(_, data) => setFilter(data.value || '')}
          />
          <PrimaryButton
            onClick={onAddTenantPriceListEvent}
            text={t('tenantPriceList.buttons.addPriceList')}
            onRenderIcon={() => <Add24Regular />}
            className="actionButton"
          />
        </div>
      </header>

      {props.error && (
        <MessageComponent
          message={t('tenantPriceList.props.errorMessage')}
          subMessage={props.error?.ErrorMessage}
          type={MessageBarType.error}
          itemList={props.error?.Errors}
        />
      )}

      {renderBody()}

      <TenantPriceListFormComponent
        isOpen={isOpen}
        openPanel={() => openPanel()}
        dismissPanel={() => {
          setSelectedTenantPriceList(undefined);
          dismissPanel();
        }}
        TenantPriceList={selectedTenantPriceList}
      />
    </section>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(TenantPriceListComponent as any);
